<template>
  <div class="space-y-4">
    <header class="flex items-center justify-between">
      <div>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          Review competitors ({{ pending.length }} pending)
        </h3>
        <p class="text-sm text-gray-500">
          Each row is a business found in the local area. Edit the keyword text + match type, then approve or dismiss.
        </p>
        <p class="text-xs text-gray-400 mt-1">
          <strong>Approve</strong> adds selected rows as negative keywords on the chosen list.
          <strong>Dismiss</strong> hides the rows so they don't appear in future review queues.
        </p>
      </div>
      <button class="btn btn-secondary btn-sm" @click="$emit('close-review')">
        Close
      </button>
    </header>

    <div v-if="loading" class="flex items-center gap-2 text-sm text-gray-500">
      <UiSpinner :size="4" screen-reader="Loading scan results" />
      <span>Loading scan results…</span>
    </div>

    <div v-else-if="pending.length === 0"
      class="rounded-lg border border-dashed border-gray-300 p-8 text-center">
      <i class="far fa-circle-check text-3xl text-gray-300 dark:text-gray-600 mb-3" />
      <p class="text-sm font-medium text-gray-700 dark:text-gray-200">
        Nothing left to review
      </p>
      <p class="text-sm text-gray-500 mt-1">
        Every result for this scan has been approved or dismissed. Run a new scan to find more competitors.
      </p>
    </div>

    <div v-else class="space-y-3">
      <div class="flex items-center gap-2">
        <select v-model="targetListId"
          v-tooltip="'Pick the list these approved keywords will land on. Auto-create makes a new Brand Competitors list scoped to this Location.'"
          class="px-2 py-1 border border-gray-300 rounded text-sm">
          <option :value="null">
            Auto-create "Brand Competitors — [Location]"
          </option>
          <option v-for="list in locationLists" :key="list.id" :value="list.id">
            {{ list.name }}
          </option>
        </select>
        <span class="text-sm text-gray-500">
          {{ selectedIds.size }} selected
        </span>
        <div class="ml-auto flex gap-2">
          <button class="btn btn-secondary btn-sm"
            v-tooltip="'Hide the selected rows so they will not appear in future reviews.'"
            :disabled="selectedIds.size === 0" @click="dismiss">
            Dismiss
          </button>
          <button class="btn btn-primary btn-sm"
            v-tooltip="'Add the selected rows as negative keywords on the chosen list.'"
            :disabled="selectedIds.size === 0" @click="approve">
            Add as Negative Keywords
          </button>
        </div>
      </div>

      <table class="min-w-full bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <thead class="bg-gray-50 dark:bg-gray-700">
          <tr>
            <th class="w-10 px-3 py-2">
              <input type="checkbox" :checked="allSelected" @change="toggleAll">
            </th>
            <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Business
            </th>
            <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Keyword
            </th>
            <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              <span>Match</span>
              <i v-tooltip="MATCH_HEADER_TOOLTIP"
                class="far fa-circle-info text-primary-300 ml-1" />
            </th>
            <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Address
            </th>
            <th class="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
              Reviews
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
          <tr v-for="row in pending" :key="row.id">
            <td class="px-3 py-2">
              <input type="checkbox" :checked="selectedIds.has(row.id)" @change="toggle(row.id)">
            </td>
            <td class="px-3 py-2 text-sm font-medium">
              {{ row.business_name }}
            </td>
            <td class="px-3 py-2">
              <input v-model="row.keyword_edit" class="w-full px-2 py-1 border border-gray-300 rounded text-sm">
            </td>
            <td class="px-3 py-2">
              <select v-model="row.match_edit"
                v-tooltip="MATCH_TYPE_TOOLTIPS[row.match_edit] || ''"
                class="px-2 py-1 border border-gray-300 rounded text-sm">
                <option value="broad">
                  Broad
                </option>
                <option value="phrase">
                  Phrase
                </option>
                <option value="exact">
                  Exact
                </option>
              </select>
            </td>
            <td class="px-3 py-2 text-sm text-gray-500">
              {{ row.address }}
            </td>
            <td class="px-3 py-2 text-sm text-right text-gray-700">
              <span v-if="row.rating">{{ row.rating }}★</span>
              <span v-if="row.review_count"> ({{ row.review_count }})</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p v-if="error" class="text-sm text-red-600">
      {{ error }}
    </p>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import vTooltip from '~/javascript/directives/tooltip'
import UiSpinner from '~/javascript/components/ui/spinner.vue'
import { toastSuccess, toastError } from '~/javascript/utils/toast'

const MATCH_TYPE_TOOLTIPS = {
  broad: "Exported as 'Negative Broad' — blocks any query containing all the keyword's words. Most flexible.",
  phrase: "Exported as 'Negative Phrase' — blocks queries containing the keyword as an exact phrase. Balanced.",
  exact: "Exported as 'Negative Exact' — blocks only this exact query. Most precise."
}

const MATCH_HEADER_TOOLTIP = 'How this row will appear in the exported CSV.<br><b>Broad</b> = Negative Broad,<br><b>Phrase</b> = Negative Phrase,<br><b>Exact</b> = Negative Exact.'

const props = defineProps({
  locationId: { type: String, required: true },
  scanRunId: { type: String, required: true },
  csrfToken: { type: String, required: true }
})

const emit = defineEmits(['close-review', 'results-changed'])

const pending = ref([])
const locationLists = ref([])
const selectedIds = ref(new Set())
const targetListId = ref(null)
const loading = ref(false)
const error = ref(null)

const allSelected = computed(() => pending.value.length > 0 && pending.value.every((r) => selectedIds.value.has(r.id)))

const fetchAll = async() => {
  loading.value = true
  try {
    const [runRes, listsRes] = await Promise.all([
      fetch(`/ajax/locations/${props.locationId}/competitor_scan_runs/${props.scanRunId}`, { headers: { Accept: 'application/json' } }),
      fetch(`/ajax/negative_keyword_lists?scope=location&location_id=${props.locationId}`, { headers: { Accept: 'application/json' } })
    ])
    const runData = await runRes.json()
    const listsData = await listsRes.json()
    pending.value = runData.pending_results.map((r) => ({
      ...r,
      keyword_edit: r.business_name.toLowerCase(),
      match_edit: 'phrase'
    }))
    locationLists.value = listsData.lists
  } catch (e) {
    error.value = e.message
    toastError(e)
  } finally {
    loading.value = false
  }
}

const toggle = (id) => {
  if (selectedIds.value.has(id)) selectedIds.value.delete(id)
  else selectedIds.value.add(id)
  selectedIds.value = new Set(selectedIds.value)
}

const toggleAll = () => {
  if (allSelected.value) selectedIds.value.clear()
  else pending.value.forEach((r) => selectedIds.value.add(r.id))
  selectedIds.value = new Set(selectedIds.value)
}

const approve = async() => {
  error.value = null
  const selected = pending.value.filter((r) => selectedIds.value.has(r.id)).map((r) => ({
    id: r.id, keyword: r.keyword_edit, match_type: r.match_edit
  }))
  const count = selected.length
  try {
    const res = await fetch(`/ajax/locations/${props.locationId}/competitor_scan_runs/${props.scanRunId}/approve_results`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' },
      body: JSON.stringify({ selected, target_list_id: targetListId.value })
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error((data.errors || ['Approve failed']).join(', '))
    }
    toastSuccess(`Approved ${count} competitor${count === 1 ? '' : 's'} as negative keywords.`)
    emit('results-changed')
    await fetchAll()
    selectedIds.value = new Set()
  } catch (e) {
    error.value = e.message
    toastError(e)
  }
}

const dismiss = async() => {
  error.value = null
  const ids = [...selectedIds.value]
  try {
    const res = await fetch(`/ajax/locations/${props.locationId}/competitor_scan_runs/${props.scanRunId}/dismiss_results`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' },
      body: JSON.stringify({ selected_ids: ids })
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error((data.errors || ['Dismiss failed']).join(', '))
    }
    toastSuccess(`Dismissed ${ids.length} result${ids.length === 1 ? '' : 's'}.`)
    emit('results-changed')
    await fetchAll()
    selectedIds.value = new Set()
  } catch (e) {
    error.value = e.message
    toastError(e)
  }
}

onMounted(fetchAll)
</script>
